/**
 * LAGOSClimateInvestmentStructuring
 * /state/lagos/climate-investment-structuring
 *
 * Capital structuring workspace — converts SSOT entity data into bankable instruments.
 * ICMA Green Bond Principles 2021 aligned · IFRS S2 · Art.6.4 / VCM pathways
 *
 * 6-Step flow:
 *   1. Portfolio Selection
 *   2. Eligibility Mapping (ICMA)
 *   3. Capital Stack Builder
 *   4. Risk Engine
 *   5. Impact Engine
 *   6. Financial Outputs + Report Generation
 */
import React, { useState, useEffect, useCallback } from "react";
import { useLagos } from "@/context/LAGOSContext";
import { SectionCard } from "@/components/ds";
import client from "@/api/client";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, Shield, DollarSign, TrendingUp,
  Leaf, Globe, Building2, Download, Loader, Check, ChevronDown,
  ChevronUp, Info, ArrowRight, FileText, BarChart2, Layers,
  Target, Activity,
} from "lucide-react";

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmtU = (v) => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
const fmtN = (v, u = "") => {
  if (!v && v !== 0) return "—";
  const n = Number(v);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M ${u}`.trim();
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K ${u}`.trim();
  return `${n.toLocaleString()} ${u}`.trim();
};
const fmtPct = (v) => (v !== undefined && v !== null ? `${Number(v).toFixed(1)}%` : "—");

// ── Step Configuration ─────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: "Portfolio Selection",  icon: Building2    },
  { id: 2, label: "Eligibility Mapping",  icon: CheckCircle2 },
  { id: 3, label: "Capital Stack",        icon: DollarSign   },
  { id: 4, label: "Risk Engine",          icon: Shield       },
  { id: 5, label: "Impact Engine",        icon: Leaf         },
  { id: 6, label: "Financial Outputs",    icon: TrendingUp   },
];

const RISK_COLORS = {
  High:   { bg: "#fef2f2", border: "#fca5a5", text: "#b91c1c", dot: "#ef4444" },
  Medium: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e", dot: "#f59e0b" },
  Low:    { bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46", dot: "#10b981" },
};

// ── Main Component ─────────────────────────────────────────────────────────────
export default function LAGOSClimateInvestmentStructuring() {
  const {
    selectedPeriod, periods, setSelectedPeriod,
    portfolio: ctxPortfolio, loading: ctxLoading,
  } = useLagos();

  // Sector / LGA / CPA filter options
  const [options,    setOptions]    = useState({ sectors: [], lgas: [], cpa_groups: [] });
  const [optLoading, setOptLoading] = useState(false);

  // Portfolio selection state (persists across all steps)
  const [selSectors, setSelSectors] = useState([]);
  const [selLgas,    setSelLgas]    = useState([]);
  const [selCpas,    setSelCpas]    = useState([]);

  // Capital stack (persists across all steps)
  const [debtPct,   setDebtPct]   = useState(60);
  const [equityPct, setEquityPct] = useState(25);
  const [concPct,   setConcPct]   = useState(15);
  const [tenor,     setTenor]     = useState(7);

  // Analysis output
  const [analysis,        setAnalysis]        = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // UI state
  const [activeStep,   setActiveStep]   = useState(1);
  const [downloading,  setDownloading]  = useState(null);
  const [expandedRisk, setExpandedRisk] = useState(null);

  // Load sector/LGA/CPA options
  useEffect(() => {
    if (!selectedPeriod) return;
    setOptLoading(true);
    client.get(`/lagos/financing/sector-options/${selectedPeriod}`)
      .then(r => setOptions(r.data || { sectors: [], lgas: [], cpa_groups: [] }))
      .catch(() => {})
      .finally(() => setOptLoading(false));
  }, [selectedPeriod]);

  // Run analysis
  const runAnalysis = useCallback(async (overrideStack) => {
    if (!selectedPeriod) return;
    setAnalysisLoading(true);
    const stack = overrideStack || { debt_pct: debtPct, equity_pct: equityPct, concessional_pct: concPct, tenor_yr: tenor };
    const filters = {};
    if (selSectors.length) filters.sectors    = selSectors;
    if (selLgas.length)    filters.lgas       = selLgas;
    if (selCpas.length)    filters.cpa_groups = selCpas;
    try {
      const r = await client.post(`/lagos/financing/analyze/${selectedPeriod}`, {
        filters, capital_stack: stack,
      });
      setAnalysis(r.data);
    } catch {
      toast.error("Analysis failed — please try again");
    } finally {
      setAnalysisLoading(false);
    }
  }, [selectedPeriod, selSectors, selLgas, selCpas, debtPct, equityPct, concPct, tenor]);

  // PDF download (passes capital stack as query params)
  const downloadPdf = async (type) => {
    if (!selectedPeriod) return;
    setDownloading(type);
    const params = `?debt_pct=${debtPct}&equity_pct=${equityPct}&conc_pct=${concPct}&tenor_yr=${tenor}`;
    const pathMap = {
      "green-bond": "green-bond-framework",
      "allocation": "allocation-report",
      "impact":     "impact-report",
    };
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/lagos/pdf/${pathMap[type]}/${selectedPeriod}${params}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` } }
      );
      if (!r.ok) throw new Error(r.statusText);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Lagos_${type}_${selectedPeriod}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Report generation failed");
    } finally {
      setDownloading(null);
    }
  };

  const toggleFilter = (arr, setArr, val) =>
    setArr(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const goToStep = (n) => {
    if (n > 1 && !analysis) { toast.info("Run portfolio analysis first"); return; }
    setActiveStep(n);
  };

  if (ctxLoading) return <div className="p-8 text-slate-400 text-sm">Loading platform data…</div>;

  const port = analysis?.portfolio        || {};
  const icma = analysis?.icma_eligibility || {};
  const cap  = analysis?.capital_stack    || {};
  const risk = analysis?.risk             || {};
  const imp  = analysis?.impact           || {};
  const fin  = analysis?.financials       || {};
  const hasAnalysis = !!analysis;

  // Summary bar — uses analysis if present, falls back to ctx
  const sumEntities  = port.total_entities   || ctxPortfolio?.total_entities   || 0;
  const sumCapex     = port.indicative_capex || 0;
  const sumReduction = port.total_reduction  || ctxPortfolio?.reduction_potential_tco2e || 0;
  const sumEligible  = icma.eligible_pct != null ? `${icma.eligible_pct}%` : "—";

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-5">

      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">
            Lagos State Government
          </span>
          <h1
            className="text-2xl font-black text-slate-900 mt-1.5"
            data-testid="page-title"
          >
            Climate Investment Structuring
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Convert SSOT portfolio data into bankable instruments · ICMA GBP 2021 · IFRS S2 · Art.6.4 / VCM
          </p>
        </div>
        <select
          value={selectedPeriod || ""}
          onChange={e => setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white"
          data-testid="period-selector"
        >
          {periods.map(p => <option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {/* ── Summary Bar — Always Visible ─────────────────────────────────────── */}
      <div className="bg-[#0f172a] rounded-xl p-5 text-white" data-testid="summary-bar">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">
          Active Portfolio Snapshot
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            ["Entities in Scope",        String(sumEntities)],
            ["Indicative Capex",         fmtU(sumCapex)],
            ["GHG Reduction Pipeline",   fmtN(sumReduction, "tCO₂e/yr")],
            ["ICMA Eligible",            sumEligible],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="text-[9px] opacity-40 uppercase tracking-wider">{k}</p>
              <p className="text-xl font-black mt-0.5">{v}</p>
            </div>
          ))}
        </div>
        {(selSectors.length > 0 || selLgas.length > 0 || selCpas.length > 0) && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/10">
            {selSectors.map(s => (
              <span key={s} className="text-[10px] bg-white/10 px-2 py-0.5 rounded capitalize">
                {s.replace(/_/g, " ")}
              </span>
            ))}
            {selLgas.map(l => (
              <span key={l} className="text-[10px] bg-emerald-900/60 border border-emerald-800 px-2 py-0.5 rounded">
                {l}
              </span>
            ))}
            {selCpas.map(c => (
              <span key={c} className="text-[10px] bg-blue-900/60 border border-blue-800 px-2 py-0.5 rounded">
                {c.replace("CPA-", "").replace(/-/g, " ")}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Step Navigation ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-1.5 flex gap-1 overflow-x-auto" data-testid="step-nav">
        {STEPS.map(s => {
          const Icon   = s.icon;
          const done   = hasAnalysis && s.id < activeStep;
          const active = activeStep === s.id;
          const locked = s.id > 1 && !hasAnalysis;
          return (
            <button
              key={s.id}
              onClick={() => goToStep(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium flex-shrink-0 transition-all ${
                active   ? "bg-[#0f172a] text-white"
                : done   ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                : locked ? "text-slate-300 cursor-not-allowed"
                         : "text-slate-500 hover:bg-slate-50"
              }`}
              data-testid={`step-btn-${s.id}`}
            >
              {done ? <Check size={11} /> : <Icon size={11} />}
              <span className="hidden sm:inline">{s.label}</span>
              <span className="sm:hidden font-bold">{s.id}</span>
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 1 — Portfolio Selection
      ══════════════════════════════════════════════════════════════════════ */}
      {activeStep === 1 && (
        <div className="space-y-4" data-testid="step-1-content">
          <SectionCard title="Portfolio Scope — Define entities to structure">
            <div className="p-5 space-y-6">

              {/* Sector filters */}
              <div>
                <p className="text-xs font-bold text-slate-700 mb-2">Filter by Sector</p>
                <div className="flex flex-wrap gap-2">
                  {optLoading
                    ? <p className="text-xs text-slate-400">Loading…</p>
                    : options.sectors.map(s => (
                      <button
                        key={s}
                        onClick={() => toggleFilter(selSectors, setSelSectors, s)}
                        className={`text-[11px] px-3 py-1.5 rounded-lg border font-medium transition-all capitalize ${
                          selSectors.includes(s)
                            ? "bg-emerald-700 text-white border-emerald-700"
                            : "bg-white text-slate-500 border-slate-200 hover:border-emerald-400 hover:text-emerald-700"
                        }`}
                        data-testid={`sector-filter-${s}`}
                      >
                        {s.replace(/_/g, " ")}
                      </button>
                    ))
                  }
                </div>
              </div>

              {/* LGA filters */}
              <div>
                <p className="text-xs font-bold text-slate-700 mb-2">Filter by LGA</p>
                <div className="flex flex-wrap gap-2">
                  {options.lgas.map(l => (
                    <button
                      key={l}
                      onClick={() => toggleFilter(selLgas, setSelLgas, l)}
                      className={`text-[11px] px-3 py-1.5 rounded-lg border font-medium transition-all ${
                        selLgas.includes(l)
                          ? "bg-blue-700 text-white border-blue-700"
                          : "bg-white text-slate-500 border-slate-200 hover:border-blue-400 hover:text-blue-700"
                      }`}
                      data-testid={`lga-filter-${l}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              {/* CPA group filters */}
              <div>
                <p className="text-xs font-bold text-slate-700 mb-2">Filter by CPA Group</p>
                <div className="flex flex-wrap gap-2">
                  {options.cpa_groups.map(c => (
                    <button
                      key={c}
                      onClick={() => toggleFilter(selCpas, setSelCpas, c)}
                      className={`text-[11px] px-3 py-1.5 rounded-lg border font-medium transition-all ${
                        selCpas.includes(c)
                          ? "bg-purple-700 text-white border-purple-700"
                          : "bg-white text-slate-500 border-slate-200 hover:border-purple-400 hover:text-purple-700"
                      }`}
                      data-testid={`cpa-filter-${c}`}
                    >
                      {c.replace("CPA-", "").replace(/-/g, " ")}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scope note */}
              <div className="flex items-start gap-2 text-xs text-slate-400 bg-slate-50 rounded-lg p-3">
                <Info size={12} className="flex-shrink-0 mt-0.5" />
                <p>
                  No filters selected = all VCM/Art.6.4 eligible entities are included automatically.
                  Selection is persistent across all structuring steps.
                </p>
              </div>

              {/* Action */}
              <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                <button
                  onClick={async () => { await runAnalysis(); if (!analysisLoading) setActiveStep(2); }}
                  disabled={analysisLoading}
                  className="flex items-center gap-2 bg-[#0f172a] text-white px-6 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800 disabled:opacity-60 transition-all"
                  data-testid="run-analysis-btn"
                >
                  {analysisLoading
                    ? <><Loader size={13} className="animate-spin" /> Running Analysis…</>
                    : <><ArrowRight size={13} /> Analyze Portfolio</>
                  }
                </button>
                {(selSectors.length > 0 || selLgas.length > 0 || selCpas.length > 0) && (
                  <button
                    onClick={() => { setSelSectors([]); setSelLgas([]); setSelCpas([]); }}
                    className="text-xs text-slate-400 hover:text-red-500 px-3 py-2 transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          </SectionCard>

          {/* Post-analysis quick stats */}
          {hasAnalysis && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                ["Entities in Scope",   String(port.total_entities),                     "#0f172a"],
                ["Sectors Covered",     String((port.sectors || []).length),              "#1d4ed8"],
                ["LGAs Represented",    String((port.lgas || []).length),                 "#059669"],
                ["Avg Data Confidence", fmtPct(port.avg_confidence),                      "#d97706"],
              ].map(([k, v, c]) => (
                <div key={k} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                  <p className="text-xs text-slate-400">{k}</p>
                  <p className="text-2xl font-black mt-1" style={{ color: c }}>{v}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 2 — ICMA Eligibility Mapping
      ══════════════════════════════════════════════════════════════════════ */}
      {activeStep === 2 && (
        <div className="space-y-4" data-testid="step-2-content">
          <SectionCard title="ICMA Green Bond Principle — Eligibility Assessment">
            <div className="p-5 space-y-5">

              {/* Three-tier eligibility KPIs */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  ["Fully Eligible",      `${icma.fully_eligible_count || 0} entities`,    `${icma.fully_eligible_pct || 0}% · T1/T2 evidence · bond-ready`,          "#059669"],
                  ["Partially Eligible",  `${icma.partially_eligible_count || 0} entities`, `${icma.partially_eligible_pct || 0}% · eligible subject to data upgrade`,  "#d97706"],
                  ["Non-Eligible",        `${icma.ineligible_count || 0} entities`,          `${icma.ineligible_pct || 0}% · outside ICMA category mapping`,            "#94a3b8"],
                ].map(([k, v, sub, c]) => (
                  <div key={k} className="rounded-xl border p-4" style={{ borderColor: `${c}33`, background: `${c}08` }}>
                    <p className="text-xs text-slate-500 font-medium">{k}</p>
                    <p className="text-2xl font-black mt-1" style={{ color: c }}>{v}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>
                  </div>
                ))}
              </div>

              {/* Eligibility rationale */}
              {icma.eligibility_rationale && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  <p className="text-xs font-bold text-slate-700">Eligibility Rationale</p>
                  {Object.entries(icma.eligibility_rationale).map(([tier, reason]) => (
                    <div key={tier} className="flex items-start gap-2 text-xs">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1 ${tier === 'fully_eligible' ? 'bg-emerald-500' : tier === 'partially_eligible' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                      <div>
                        <span className="font-bold text-slate-600 capitalize">{tier.replace(/_/g, ' ')}: </span>
                        <span className="text-slate-500">{reason}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ICMA Category Table */}
              <div>
                <p className="text-xs font-bold text-slate-700 mb-3">ICMA Category Breakdown</p>
                <div className="overflow-x-auto rounded-xl border border-slate-100">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        {["ICMA Category", "Fully Eligible", "Partially Eligible", "GHG Reduction", "Capex", "% of Total"].map(h => (
                          <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(icma.categories || {}).map(([cat, data], i) => (
                        <tr key={cat} className={`border-b border-slate-50 hover:bg-slate-50 ${i % 2 === 0 ? "" : "bg-slate-50/40"}`}>
                          <td className="py-2.5 px-3 font-medium text-slate-700">{cat}</td>
                          <td className="py-2.5 px-3 text-emerald-700 font-bold">{data.full_count || 0}</td>
                          <td className="py-2.5 px-3 text-amber-600 font-bold">{data.partial_count || 0}</td>
                          <td className="py-2.5 px-3">{fmtN(data.reduction, "tCO₂e/yr")}</td>
                          <td className="py-2.5 px-3 font-bold">{fmtU(data.capex)}</td>
                          <td className="py-2.5 px-3">
                            {icma.eligible_capex
                              ? `${((data.capex / icma.eligible_capex) * 100).toFixed(1)}%`
                              : "—"}
                          </td>
                        </tr>
                      ))}
                      {Object.keys(icma.categories || {}).length === 0 && (
                        <tr><td colSpan={6} className="py-4 px-3 text-center text-slate-400">No ICMA-eligible categories found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Coverage indicators */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-800 mb-3">Eligibility Coverage Summary</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">% Eligible by Entity Count</p>
                    <p className="text-2xl font-black text-blue-700 mt-0.5">{fmtPct(icma.eligible_pct)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">% Eligible by Capex</p>
                    <p className="text-2xl font-black text-blue-700 mt-0.5">
                      {port.indicative_capex && icma.eligible_capex
                        ? fmtPct((icma.eligible_capex / port.indicative_capex) * 100)
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">ICMA Categories Covered</p>
                    <p className="text-2xl font-black text-blue-700 mt-0.5">
                      {Object.keys(icma.categories || {}).length}
                    </p>
                  </div>
                </div>
              </div>

              {/* Flagged assets warning */}
              {(icma.ineligible_count || 0) > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-amber-800">
                        {icma.ineligible_count} entities excluded — outside ICMA Green Bond category mapping
                      </p>
                      <p className="text-[10px] text-amber-700 mt-1">
                        These entities may qualify under Social Bonds, Sustainability-Linked Loans, or other instruments.
                        They are excluded from bond allocation but retained in impact reporting scope.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                <button onClick={() => setActiveStep(1)} className="text-xs text-slate-400 hover:text-slate-600 px-3 py-2">
                  ← Back
                </button>
                <button
                  onClick={() => setActiveStep(3)}
                  className="flex items-center gap-2 bg-[#0f172a] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800"
                  data-testid="step2-next"
                >
                  Next: Capital Stack <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 3 — Capital Stack Builder
      ══════════════════════════════════════════════════════════════════════ */}
      {activeStep === 3 && (
        <div className="space-y-4" data-testid="step-3-content">
          <SectionCard title="Capital Stack Builder — Structure the Financing Package">
            <div className="p-5 space-y-6">

              {/* Sliders */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {[
                  ["Senior Debt / Bond",   debtPct,   setDebtPct,   "#1d4ed8", "Bond proceeds to capital markets"],
                  ["Private Equity",       equityPct, setEquityPct, "#059669", "Co-investment / Lagos State budget"],
                  ["Concessional Finance", concPct,   setConcPct,   "#7c3aed", "DFI / blended finance (sub-tranche of debt)"],
                ].map(([label, val, setter, color, desc]) => (
                  <div key={label} className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-baseline">
                      <p className="text-xs font-bold text-slate-700">{label}</p>
                      <p className="text-2xl font-black" style={{ color }}>{val}%</p>
                    </div>
                    <input
                      type="range" min={0} max={100} step={1} value={val}
                      onChange={e => setter(Number(e.target.value))}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{ accentColor: color }}
                      data-testid={`slider-${label.split(" ")[0].toLowerCase()}`}
                    />
                    <p className="text-[10px] text-slate-400">{desc}</p>
                    <p className="text-sm font-black text-slate-800">
                      {fmtU((cap.eligible_capex || 0) * val / 100)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Tenor Selector */}
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-bold text-slate-700 mb-3">Bond Tenor — DSCR Sensitivity</p>
                <div className="flex gap-2 mb-3">
                  {[5, 7, 10, 15].map(t => (
                    <button
                      key={t}
                      onClick={() => setTenor(t)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-black border transition-all ${
                        tenor === t
                          ? "bg-[#0f172a] text-white border-[#0f172a]"
                          : "bg-white text-slate-500 border-slate-200 hover:border-slate-400 hover:text-slate-700"
                      }`}
                      data-testid={`tenor-${t}`}
                    >
                      {t}yr
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400">
                  Tenor affects annual debt service and DSCR. See Financial Outputs (Step 6) for full sensitivity table.
                </p>
              </div>

              {/* Derived values */}
              <div className="bg-[#0f172a] rounded-xl p-5 text-white">
                <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-4">
                  Derived Financing Package
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    ["Total Capex Required",    fmtU(cap.total_capex),     "#94a3b8"],
                    ["ICMA-Eligible Capex",      fmtU(cap.eligible_capex),  "#34d399"],
                    ["Indicative Bond Size",     fmtU(cap.bond_size),       "#60a5fa"],
                    ["Total Financing Package",  fmtU(cap.total_finance),   "#f9fafb"],
                  ].map(([k, v, c]) => (
                    <div key={k}>
                      <p className="text-[10px] opacity-40">{k}</p>
                      <p className="text-lg font-black mt-0.5" style={{ color: c }}>{v}</p>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/10">
                  {[
                    ["Concessional Tranche",  fmtU(cap.concessional),      "#c4b5fd"],
                    ["Commercial Tranche",    fmtU(cap.commercial),        "#7dd3fc"],
                    ["Equity Contribution",   fmtU(cap.equity),            "#6ee7b7"],
                  ].map(([k, v, c]) => (
                    <div key={k}>
                      <p className="text-[10px] opacity-40">{k}</p>
                      <p className="text-base font-black mt-0.5" style={{ color: c }}>{v}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bond size warning */}
              {cap.bond_size > 0 && cap.bond_size < 5e6 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
                  <AlertTriangle size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">
                    Indicative bond size is below $5M — standalone bond issuance may not be cost-effective at this scale.
                    Consider aggregating additional LGAs/sectors, or explore a green loan or sustainability-linked facility.
                  </p>
                </div>
              )}

              {/* Bond sizing rationale */}
              {cap.bond_size > 0 && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <p className="text-xs font-bold text-blue-800 mb-3">Bond Sizing Rationale</p>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      ["Bond as % of Eligible Capex", `${cap.bond_pct_of_eligible_capex || 0}%`, "#1d4ed8"],
                      ["Bond as % of Total Capex",    `${cap.bond_pct_of_total_capex || 0}%`,    "#0891b2"],
                      ["Unfunded Capital Gap",         fmtU(cap.remaining_gap || 0),              "#d97706"],
                    ].map(([k, v, c]) => (
                      <div key={k}>
                        <p className="text-[10px] text-slate-500">{k}</p>
                        <p className="text-xl font-black mt-0.5" style={{ color: c }}>{v}</p>
                      </div>
                    ))}
                  </div>
                  {cap.sizing_rationale && (
                    <p className="text-[10px] text-blue-700 mt-3 leading-relaxed">{cap.sizing_rationale}</p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="flex gap-3">
                  <button onClick={() => setActiveStep(2)} className="text-xs text-slate-400 hover:text-slate-600 px-3 py-2">
                    ← Back
                  </button>
                  <button
                    onClick={() => runAnalysis({ debt_pct: debtPct, equity_pct: equityPct, concessional_pct: concPct, tenor_yr: tenor })}
                    disabled={analysisLoading}
                    className="flex items-center gap-2 border border-slate-300 text-slate-600 px-4 py-2 rounded-lg text-xs font-bold hover:bg-slate-50 disabled:opacity-60"
                    data-testid="recalculate-btn"
                  >
                    {analysisLoading ? <Loader size={11} className="animate-spin" /> : <BarChart2 size={11} />}
                    {analysisLoading ? "Recalculating…" : "Recalculate Stack"}
                  </button>
                </div>
                <button
                  onClick={() => setActiveStep(4)}
                  className="flex items-center gap-2 bg-[#0f172a] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800"
                  data-testid="step3-next"
                >
                  Next: Risk Engine <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 4 — Risk Engine
      ══════════════════════════════════════════════════════════════════════ */}
      {activeStep === 4 && (
        <div className="space-y-4" data-testid="step-4-content">
          <SectionCard title="Risk Engine — Portfolio Risk Assessment">
            <div className="p-5 space-y-5">

              {/* Risk KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  ["Risk Score",     `${Math.round(risk.risk_score || 0)}/100`,  risk.risk_score >= 70 ? "#059669" : risk.risk_score >= 50 ? "#d97706" : "#ef4444"],
                  ["Risk Rating",    risk.risk_rating || "—",                    risk.risk_rating === "Low" ? "#059669" : risk.risk_rating === "Medium" ? "#d97706" : "#ef4444"],
                  ["Data Confidence", fmtPct(risk.data_conf_avg),                "#1d4ed8"],
                  ["ICMA Eligibility", fmtPct(risk.eligible_pct),               "#7c3aed"],
                ].map(([k, v, c]) => (
                  <div key={k} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                    <p className="text-xs text-slate-400">{k}</p>
                    <p className="text-2xl font-black mt-1" style={{ color: c }}>{v}</p>
                  </div>
                ))}
              </div>

              {/* 5 Risk Dimensions */}
              <div>
                <p className="text-xs font-bold text-slate-700 mb-3">Five Risk Dimensions</p>
                <div className="space-y-2">
                  {[
                    {
                      dim: "Data Confidence Risk",
                      severity: risk.data_conf_avg >= 65 ? "Low" : risk.data_conf_avg >= 45 ? "Medium" : "High",
                      detail: `Portfolio average confidence: ${fmtPct(risk.data_conf_avg)}. Target ≥65 for VCM validation.`,
                      mitigation: "Upgrade top 20 entities to T2 measured evidence before PDD submission.",
                    },
                    {
                      dim: "Concentration Risk",
                      severity: (risk.sectors_count || 0) >= 5 ? "Low" : (risk.sectors_count || 0) >= 3 ? "Medium" : "High",
                      detail: `${risk.sectors_count || 0} sectors present. Minimum 4–5 sectors recommended for diversification.`,
                      mitigation: "Expand to 5+ sectors. Ensure no single sector exceeds 40% of bond allocation.",
                    },
                    {
                      dim: "Regulatory Dependency Risk (Art.6.4)",
                      severity: (risk.art6_pct || 0) > 40 ? "High" : (risk.art6_pct || 0) > 20 ? "Medium" : "Low",
                      detail: `Art.6.4 entities = ${fmtPct(risk.art6_pct)}. NCCC authorization required for >25% of portfolio.`,
                      mitigation: "Structure VCM-first pathway. Art.6.4 as conditional upside tranche only.",
                    },
                    {
                      dim: "ICMA Eligibility Gap Risk",
                      severity: (icma.eligible_pct || 0) >= 70 ? "Low" : (icma.eligible_pct || 0) >= 50 ? "Medium" : "High",
                      detail: `${fmtPct(icma.eligible_pct)} ICMA-eligible. Target ≥70% for a credible green bond.`,
                      mitigation: "Formalise informal operators. Upgrade evidence tier. Expand ICMA category mapping.",
                    },
                    {
                      dim: "Execution & Readiness Risk",
                      severity: risk.risk_rating === "Low" ? "Low" : risk.risk_rating === "Medium" ? "Medium" : "High",
                      detail: "Readiness depends on DOE engagement, MRV infrastructure, and Lagos State programme office capacity.",
                      mitigation: "Engage accredited DOE. Establish Lagos State green bond programme office. Prioritise T1 MRV infrastructure.",
                    },
                  ].map(({ dim, severity, detail, mitigation }) => {
                    const col    = RISK_COLORS[severity] || RISK_COLORS.Medium;
                    const isOpen = expandedRisk === dim;
                    return (
                      <div
                        key={dim}
                        className="rounded-xl border overflow-hidden"
                        style={{ borderColor: col.border, background: col.bg }}
                      >
                        <button
                          className="w-full flex items-center justify-between p-3.5 text-left"
                          onClick={() => setExpandedRisk(isOpen ? null : dim)}
                          data-testid={`risk-${dim.substring(0, 10)}`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: col.dot }} />
                            <p className="text-xs font-bold" style={{ color: col.text }}>{dim}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className="text-[10px] px-2 py-0.5 rounded-full font-bold text-white"
                              style={{ background: col.dot }}
                            >
                              {severity}
                            </span>
                            {isOpen
                              ? <ChevronUp size={12} style={{ color: col.text }} />
                              : <ChevronDown size={12} style={{ color: col.text }} />
                            }
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 space-y-2 border-t" style={{ borderColor: col.border }}>
                            <p className="text-xs text-slate-600 mt-3">{detail}</p>
                            <div className="flex items-start gap-2 bg-white/70 rounded-lg p-3">
                              <Check size={11} className="flex-shrink-0 mt-0.5" style={{ color: col.dot }} />
                              <p className="text-xs" style={{ color: col.text }}>
                                <strong>Mitigation:</strong> {mitigation}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Engine-generated flags */}
              {(risk.flags || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold text-slate-700 mb-2">Engine Risk Flags</p>
                  <div className="space-y-2">
                    {risk.flags.map(f => {
                      const col = RISK_COLORS[f.severity] || RISK_COLORS.Medium;
                      return (
                        <div
                          key={f.flag}
                          className="rounded-xl border p-3.5"
                          style={{ borderColor: col.border, background: col.bg }}
                          data-testid={`flag-${f.flag}`}
                        >
                          <div className="flex items-start gap-2.5">
                            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" style={{ color: col.dot }} />
                            <div>
                              <p className="text-xs font-bold" style={{ color: col.text }}>{f.flag}</p>
                              <p className="text-[10px] text-slate-600 mt-0.5">{f.description}</p>
                              <p className="text-[10px] mt-1 font-medium" style={{ color: col.text }}>
                                Mitigation: {f.mitigation}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <button onClick={() => setActiveStep(3)} className="text-xs text-slate-400 hover:text-slate-600 px-3 py-2">
                  ← Back
                </button>
                <button
                  onClick={() => setActiveStep(5)}
                  className="flex items-center gap-2 bg-[#0f172a] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800"
                  data-testid="step4-next"
                >
                  Next: Impact Engine <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 5 — Impact Engine
      ══════════════════════════════════════════════════════════════════════ */}
      {activeStep === 5 && (
        <div className="space-y-4" data-testid="step-5-content">
          <SectionCard title="Impact Engine — SSOT-Derived Climate & Circularity Outcomes">
            <div className="p-5 space-y-5">

              {/* Top KPIs */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  ["GHG Reduction Pipeline", fmtN(imp.total_tco2e_yr, "tCO₂e/yr"), "#059669"],
                  ["Circularity Impact",     fmtN(imp.circularity_tco2e, "tCO₂e"),  "#0891b2"],
                  ["Entities in Scope",      String(imp.entities || 0),              "#0f172a"],
                  ["VCM Carbon Pipeline",    `${imp.vcm_pipeline || 0} entities`,    "#7c3aed"],
                ].map(([k, v, c]) => (
                  <div key={k} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                    <p className="text-xs text-slate-400">{k}</p>
                    <p className="text-xl font-black mt-1" style={{ color: c }}>{v}</p>
                  </div>
                ))}
              </div>

              {/* LGA spread + SDG coverage */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white border border-slate-100 rounded-xl p-4">
                  <p className="text-xs font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <Globe size={12} /> Geographic Spread — LGAs Covered
                  </p>
                  <div className="space-y-1.5">
                    {(port.lgas || []).map(lga => (
                      <div key={lga} className="flex items-center justify-between text-xs py-1 border-b border-slate-50">
                        <span className="text-slate-600">{lga}</span>
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">Included</span>
                      </div>
                    ))}
                    {(port.lgas || []).length === 0 && (
                      <p className="text-xs text-slate-400">No LGA data available</p>
                    )}
                  </div>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-4">
                  <p className="text-xs font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <Activity size={12} /> SDG Contribution Mapping
                  </p>
                  <div className="space-y-1.5">
                    {Object.entries(imp.sdg_coverage || {}).map(([sdgId, count]) => (
                      <div key={sdgId} className="flex items-center justify-between py-1 border-b border-slate-50">
                        <span className="text-xs font-bold text-slate-500">SDG {sdgId}</span>
                        <span className="text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                          {count} entities
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* VCM vs Art.6.4 pipeline */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
                  <p className="text-xs font-bold text-emerald-800 mb-1 flex items-center gap-2">
                    <Target size={12} /> VCM Pipeline
                  </p>
                  <p className="text-3xl font-black text-emerald-700">{imp.vcm_pipeline || 0}</p>
                  <p className="text-[10px] text-emerald-600 mt-1">entities eligible for voluntary carbon market</p>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <p className="text-xs font-bold text-blue-800 mb-1 flex items-center gap-2">
                    <Layers size={12} /> Art.6.4 / PACM Pipeline
                  </p>
                  <p className="text-3xl font-black text-blue-700">{imp.art6_pipeline || 0}</p>
                  <p className="text-[10px] text-blue-600 mt-1">conditionally eligible — NCCC authorization required</p>
                </div>
              </div>

              {/* SSOT integrity note */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <p className="text-xs font-bold text-slate-600 mb-2">SSOT Integrity Declaration</p>
                <p className="text-[10px] text-slate-500">
                  All impact values are derived exclusively from entity-level SSOT calculation snapshots stored in the
                  DeCarbonIQ Lagos platform. No top-down allocation or manual override is used. Chain of custody:
                  entity input → scope calculation → reduction derivation → portfolio aggregation → financing output.
                </p>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <button onClick={() => setActiveStep(4)} className="text-xs text-slate-400 hover:text-slate-600 px-3 py-2">
                  ← Back
                </button>
                <button
                  onClick={() => setActiveStep(6)}
                  className="flex items-center gap-2 bg-[#0f172a] text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-800"
                  data-testid="step5-next"
                >
                  Next: Financial Outputs <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 6 — Financial Outputs
      ══════════════════════════════════════════════════════════════════════ */}
      {activeStep === 6 && (
        <div className="space-y-4" data-testid="step-6-content">
          <SectionCard title="Financial Outputs — Capital Structuring Summary">
            <div className="p-5 space-y-6">

              {/* Section A: Operational / Bankable Value */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                  A — Operational / Bankable Value
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    [
                      "Circularity Operational Value",
                      fmtU(fin.operational_value_usd_yr) + "/yr",
                      "Material cost savings + circularity revenue — bankable",
                      "#059669",
                    ],
                    [
                      "Annual Debt Service",
                      fmtU(fin.annual_debt_service) + "/yr",
                      `${fin.assumptions?.coupon_rate || "5.0%"} coupon + principal (${fin.tenor_yr || tenor}yr tenor)`,
                      "#0f172a",
                    ],
                    [
                      "Operational DSCR (excl. carbon)",
                      `${fin.dscr_operational || "—"}x`,
                      "Carbon excluded from base DSCR. Target >1.2x for PFI acceptance.",
                      fin.dscr_operational >= 1.2 ? "#059669" : "#d97706",
                    ],
                  ].map(([k, v, sub, c]) => (
                    <div key={k} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                      <p className="text-xs text-slate-400">{k}</p>
                      <p className="text-xl font-black mt-1" style={{ color: c }}>{v}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{sub}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* DSCR Sensitivity Table */}
              {fin.dscr_sensitivity && (
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-slate-700 mb-1">Operational DSCR Sensitivity — Tenor (excluding carbon)</p>
                  <p className="text-[10px] text-slate-400 mb-3">
                    Assumptions: {fin.assumptions?.coupon_rate || "5.0%"} coupon · operational revenue = circularity credit value ($15/t) · carbon excluded
                  </p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200">
                        {["Tenor", "Annual Debt Service", "DSCR — Operational", "DSCR — with Carbon (sensitivity)"].map(h => (
                          <th key={h} className="text-left py-2 px-2 text-slate-500 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(fin.dscr_sensitivity).map(row => {
                        const isCurrent = row.tenor_yr === (fin.tenor_yr || tenor);
                        return (
                          <tr
                            key={row.tenor_yr}
                            className={`border-b border-slate-50 ${isCurrent ? "bg-blue-50" : "hover:bg-slate-50"}`}
                            data-testid={`dscr-row-${row.tenor_yr}`}
                          >
                            <td className="py-2 px-2 font-black">
                              {row.tenor_yr}yr
                              {isCurrent && <span className="ml-2 text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">Selected</span>}
                            </td>
                            <td className="py-2 px-2">{fmtU(row.annual_debt_service)}/yr</td>
                            <td className="py-2 px-2 font-black" style={{ color: row.dscr_op >= 1.2 ? "#059669" : "#d97706" }}>
                              {row.dscr_op}x
                            </td>
                            <td className="py-2 px-2 text-slate-400">{row.dscr_with_carbon}x <span className="text-[9px]">(sensitivity)</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex items-start gap-2 mt-3 pt-3 border-t border-slate-100">
                    <Info size={11} className="text-slate-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[10px] text-slate-400">
                      Carbon value is shown as sensitivity only and is NOT included in base DSCR per ICMA green bond best practice.
                      Carbon revenue is excluded from all PFI commitment letters until formally VVB-validated and registry-issued.
                    </p>
                  </div>
                </div>
              )}

              {/* Section B: Carbon Value */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">
                  B — Carbon Value (Indicative — NOT Bankable Until Validated)
                </p>
                <p className="text-[10px] text-slate-400 mb-3">
                  VCM and Art.6.4 carbon value excluded from base DSCR. Shown as sensitivity only.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    ["Low  ($10/t — VCM floor)",  fmtU(fin.carbon_low_usd_yr)  + "/yr", "#64748b", false],
                    ["Base ($20/t — VCM spot)",   fmtU(fin.carbon_base_usd_yr) + "/yr", "#059669", false],
                    ["High ($40/t — Art.6.4)",    fmtU(fin.carbon_high_usd_yr) + "/yr", "#1d4ed8", true],
                  ].map(([k, v, c, art6]) => (
                    <div key={k} className="rounded-xl border p-4" style={{ background: `${c}08`, borderColor: `${c}33` }}>
                      <p className="text-xs text-slate-500">{k}</p>
                      <p className="text-xl font-black mt-1" style={{ color: c }}>{v}</p>
                      {art6 && (
                        <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded mt-1.5 inline-block font-bold">
                          CONDITIONAL — NCCC required
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Section C: Structuring Indicators */}
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">
                  C — Structuring Indicators
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-100 rounded-xl p-4 space-y-2">
                    {[
                      ["Indicative Bond Size",                      fmtU(cap.bond_size)],
                      ["Concessional Tranche",                       fmtU(cap.concessional)],
                      ["Operational DSCR (excl. carbon)",           `${fin.dscr_operational || "—"}x`],
                      ["DSCR with Carbon — Sensitivity Only",       `${fin.dscr_with_carbon || "—"}x`],
                      ["Blended Payback Range",                     "24–48 months (sector-dependent)"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between items-center py-1.5 border-b border-slate-50">
                        <span className="text-xs text-slate-500">{k}</span>
                        <span className="text-sm font-black text-slate-800">{v}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                    <p className="text-xs font-bold text-slate-600 mb-3">Use-of-Proceeds Allocation</p>
                    <p className="text-[10px] text-slate-400 mb-3">Bond proceeds by ICMA category ({debtPct}% debt tranche)</p>
                    <div className="space-y-2">
                      {Object.entries(icma.categories || {}).slice(0, 6).map(([cat, data]) => (
                        <div key={cat} className="flex justify-between items-center">
                          <span className="text-[10px] text-slate-500 truncate max-w-[180px]">{cat}</span>
                          <span className="text-xs font-bold text-blue-700">{fmtU(data.capex * debtPct / 100)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Mandatory disclosure */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-amber-800">Mandatory Disclosure — Carbon Asset Treatment</p>
                    <p className="text-[10px] text-amber-700 mt-1 leading-relaxed">
                      Carbon asset values are SSOT-derived indicative estimates only. They are NOT treated as
                      guaranteed or committed cash flows until: (a) validated by an accredited VVB against an approved
                      methodology, (b) registered with Verra VCS, Gold Standard, or NCCC, and (c) formally issued as
                      carbon credits. Carbon value MUST NOT be included in DSCR calculations or PFI financing
                      commitments until formally validated and authorized. Art.6.4 pathway is conditional on NCCC
                      sovereign authorization.
                    </p>
                  </div>
                </div>
              </div>

              {/* Report Generation */}
              <div>
                <p className="text-xs font-bold text-slate-700 mb-1">Generate Institutional Reports</p>
                <p className="text-[10px] text-slate-400 mb-4">
                  All reports are dynamic, SSOT-driven, QR-verifiable, and ICMA GBP 2021 aligned.
                  Capital stack parameters are embedded in the generated document.
                </p>
                <div className="space-y-3">
                  {[
                    {
                      key:    "green-bond",
                      label:  "Green Bond Framework",
                      badge:  "ICMA GBP 2021",
                      badgeC: "bg-emerald-100 text-emerald-700",
                      desc:   "Institutional framework document: Use of Proceeds, Project Evaluation, Proceeds Management, Reporting. Full navy cover, QR-verified. ~14 pages.",
                    },
                    {
                      key:    "allocation",
                      label:  "Allocation Report",
                      badge:  "Use of Proceeds",
                      badgeC: "bg-blue-100 text-blue-700",
                      desc:   "Entity-level capital allocation by ICMA category. Ring-fenced proceeds management confirmation. Sector and LGA breakdown. QR-verifiable.",
                    },
                    {
                      key:    "impact",
                      label:  "Impact Report",
                      badge:  "Climate Impact",
                      badgeC: "bg-purple-100 text-purple-700",
                      desc:   "Annual climate outcomes: tCO₂e reduction, circularity impact, SDG contribution, LGA geographic spread. IFRS S2 aligned. QR-verifiable.",
                    },
                  ].map(r => (
                    <div
                      key={r.key}
                      className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-start justify-between gap-4"
                      data-testid={`report-card-${r.key}`}
                    >
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText size={16} className="text-slate-400" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="text-sm font-bold text-slate-800">{r.label}</p>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.badgeC}`}>{r.badge}</span>
                          </div>
                          <p className="text-xs text-slate-500">{r.desc}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadPdf(r.key)}
                        disabled={downloading === r.key || !selectedPeriod}
                        className="flex items-center gap-2 bg-[#1d4ed8] text-white text-xs px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-60 flex-shrink-0 min-w-[130px] justify-center font-bold"
                        data-testid={`download-${r.key}`}
                      >
                        {downloading === r.key
                          ? <><Loader size={11} className="animate-spin" /> Generating…</>
                          : <><Download size={11} /> Download PDF</>
                        }
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-start pt-2 border-t border-slate-100">
                <button onClick={() => setActiveStep(5)} className="text-xs text-slate-400 hover:text-slate-600 px-3 py-2">
                  ← Back to Impact Engine
                </button>
              </div>
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
